import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Alert, Avatar, Button, Card, Empty, Segmented, Skeleton, Space, Statistic, Table, Tabs, Tag, Typography,
} from 'antd';
import {
  BookOutlined, CalendarOutlined, ExclamationCircleOutlined, FileOutlined, HistoryOutlined,
  SafetyCertificateOutlined, TrophyOutlined, CarOutlined, UserOutlined,
} from '@ant-design/icons';
import type {
  GuardianAttendanceEntry, GuardianBroadcast, GuardianInvoiceRow, StudentHomeworkRow,
} from '@velora/contracts';
import { productApi } from '../../api';

/**
 * Guardian — one ward's detail. Tabs: Attendance / Fees / Broadcasts.
 * Everything read-only for now; fee payments and broadcast-open receipts land
 * with the paywall + push-notification integration.
 */
export function GuardianWardPage() {
  const { institution = '', student = '' } = useParams<{ institution: string; student: string }>();
  const navigate = useNavigate();

  // Ward switcher — persistent chips at the top of the ward page.
  const home = useQuery({
    queryKey: ['guardian-home', institution],
    queryFn: () => productApi.getGuardianHome(institution),
    enabled: !!institution,
  });

  const s = useQuery({
    queryKey: ['guardian-ward', institution, student],
    queryFn: () => productApi.getGuardianWardSummary(institution, student),
    enabled: !!institution && !!student,
    retry: false,
  });

  if (s.isLoading) return <Skeleton active paragraph={{ rows: 6 }} />;
  if (!s.data) return <Alert type="error" showIcon message="Could not load ward" description={String(s.error)} />;

  const { ward, attendance, fees, latest_exam } = s.data;
  const wards = home.data?.wards ?? [];
  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <Button type="text" size="small" onClick={() => navigate(`/i/${institution}/parent`)}>← Home</Button>
      </div>

      {wards.length > 1 && (
        <Segmented
          block
          value={student}
          onChange={(v) => navigate(`/i/${institution}/parent/${v}`)}
          options={wards.map((w) => ({
            value: w.admission_number,
            label: (
              <span>
                {w.name.split(' ')[0]} <span style={{ opacity: 0.6, fontSize: 11 }}>{w.grade}{w.section ? `-${w.section}` : ''}</span>
              </span>
            ),
          }))}
        />
      )}

      <Card size="small">
        <Space size={12} align="center">
          <Avatar size={54} style={{ background: `hsl(${(ward.name.length * 37) % 360}, 40%, 60%)`, fontSize: 18 }}>
            {ward.name.split(/\s+/).map((p) => p[0]).slice(0, 2).join('').toUpperCase()}
          </Avatar>
          <div>
            <div style={{ fontSize: 17, fontWeight: 700 }}>{ward.name}</div>
            <div style={{ color: '#5b6478', fontSize: 12 }}>
              {ward.grade}{ward.section ? `-${ward.section}` : ''} · <span style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}>{ward.admission_number}</span>
            </div>
          </div>
        </Space>
      </Card>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 12 }}>
        <Card size="small">
          <Statistic
            title="Attendance"
            value={attendance.percent !== null ? `${attendance.percent}%` : '—'}
            valueStyle={{ color: attendance.percent !== null && attendance.percent < 85 ? '#b3382f' : '#14213d', fontSize: 20 }}
          />
          <Typography.Text type="secondary" style={{ fontSize: 11 }}>
            {attendance.present_days} present · {attendance.absent_days} absent · {attendance.marked_days} marked
          </Typography.Text>
        </Card>
        <Card size="small">
          <Statistic
            title="Fees due"
            value={`₹${paise(fees.open_paise)}`}
            valueStyle={{ color: fees.open_paise > 0 ? '#b3382f' : '#1c6442', fontSize: 20 }}
          />
          <Typography.Text type="secondary" style={{ fontSize: 11 }}>
            {fees.invoice_count_open} open invoice{fees.invoice_count_open === 1 ? '' : 's'}
            {fees.next_due_on ? ` · next due ${fees.next_due_on}` : ''}
          </Typography.Text>
        </Card>
        <Card size="small">
          <Statistic
            title={latest_exam ? latest_exam.exam_name : 'Latest exam'}
            value={latest_exam ? `${latest_exam.percentage}%` : '—'}
            valueStyle={{ color: '#14213d', fontSize: 20 }}
          />
          <Typography.Text type="secondary" style={{ fontSize: 11 }}>
            {latest_exam ? `${latest_exam.obtained} / ${latest_exam.max} · ${latest_exam.session_label}` : 'no marks yet'}
          </Typography.Text>
        </Card>
      </div>

      <Card size="small" title="Quick links">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 8 }}>
          <Link to={`/i/${institution}/parent/${student}/timetable`}><Button block icon={<CalendarOutlined />}>Timetable</Button></Link>
          <Link to={`/i/${institution}/parent/${student}/results`}><Button block icon={<TrophyOutlined />}>Results</Button></Link>
          <Link to={`/i/${institution}/parent/${student}/documents`}><Button block icon={<FileOutlined />}>Documents</Button></Link>
          <Link to={`/i/${institution}/parent/${student}/fees/history`}><Button block icon={<HistoryOutlined />}>Fees history</Button></Link>
          <Link to={`/i/${institution}/parent/${student}/year`}><Button block icon={<BookOutlined />}>Year view</Button></Link>
          <Link to={`/i/${institution}/parent/${student}/transport`}><Button block icon={<CarOutlined />}>Transport</Button></Link>
          <Link to={`/i/${institution}/parent/${student}/consents`}><Button block icon={<SafetyCertificateOutlined />}>Consents</Button></Link>
          <Link to={`/i/${institution}/parent/${student}/absence/new`}><Button block danger icon={<ExclamationCircleOutlined />}>Report absence</Button></Link>
          <Link to={`/i/${institution}/parent/profile`}><Button block icon={<UserOutlined />}>My profile</Button></Link>
        </div>
      </Card>

      <Tabs
        defaultActiveKey="attendance"
        items={[
          { key: 'attendance', label: 'Attendance', children: <AttendanceTab institution={institution} student={student} /> },
          { key: 'homework', label: 'Homework', children: <HomeworkTab institution={institution} student={student} /> },
          { key: 'fees', label: 'Fees', children: <FeesTab institution={institution} student={student} /> },
          { key: 'broadcasts', label: 'Broadcasts', children: <BroadcastsTab institution={institution} /> },
        ]}
      />
    </Space>
  );
}

function AttendanceTab({ institution, student }: { institution: string; student: string }) {
  const q = useQuery({
    queryKey: ['guardian-attendance', institution, student],
    queryFn: () => productApi.getGuardianAttendance(institution, student),
    enabled: !!institution && !!student,
  });
  if (q.isLoading) return <Skeleton active paragraph={{ rows: 4 }} />;
  const entries = q.data?.entries ?? [];
  if (entries.length === 0) return <Empty description="No attendance yet." style={{ padding: 20 }} />;
  return (
    <Table<GuardianAttendanceEntry>
      size="small"
      pagination={{ pageSize: 15 }}
      rowKey={(r) => r.date}
      dataSource={entries}
      columns={[
        { title: 'Date', dataIndex: 'date', key: 'd', render: (v: string) => <span style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 12 }}>{v}</span> },
        {
          title: 'Status', dataIndex: 'status', key: 's', width: 100,
          render: (v: string) => <Tag color={v === 'present' ? 'success' : v === 'absent' ? 'error' : 'warning'}>{v}</Tag>,
        },
        { title: 'Note', dataIndex: 'note', key: 'n', render: (v: string | null) => v ?? <Typography.Text type="secondary">—</Typography.Text> },
      ]}
    />
  );
}

function FeesTab({ institution, student }: { institution: string; student: string }) {
  const q = useQuery({
    queryKey: ['guardian-fees', institution, student],
    queryFn: () => productApi.getGuardianFees(institution, student),
    enabled: !!institution && !!student,
  });
  if (q.isLoading) return <Skeleton active paragraph={{ rows: 4 }} />;
  const inv = q.data?.invoices ?? [];
  const rec = q.data?.receipts ?? [];
  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Card size="small" title={`Invoices (${inv.length})`}>
        {inv.length === 0 ? (
          <Empty description="No invoices yet." style={{ padding: 20 }} />
        ) : (
          <Table<GuardianInvoiceRow>
            size="small"
            pagination={false}
            rowKey="invoice_id"
            dataSource={inv}
            columns={[
              { title: 'Head', dataIndex: 'fee_head_name', key: 'h' },
              { title: 'Amount', dataIndex: 'amount_paise', key: 'a', align: 'right', render: (v: number) => `₹${paise(v)}` },
              { title: 'Paid', dataIndex: 'paid_paise', key: 'p', align: 'right', render: (v: number) => `₹${paise(v)}` },
              { title: 'Due', dataIndex: 'due_on', key: 'd' },
              {
                title: 'Status', dataIndex: 'status', key: 's', width: 100,
                render: (v: string, r) => v === 'paid' ? <Tag color="success">paid</Tag> : (r.days_overdue > 0 ? <Tag color="error">{r.days_overdue}d late</Tag> : <Tag>due</Tag>),
              },
            ]}
          />
        )}
      </Card>
      <Card size="small" title={`Receipts (${rec.length})`}>
        {rec.length === 0 ? (
          <Empty description="No receipts yet." style={{ padding: 20 }} />
        ) : (
          <Table
            size="small"
            pagination={false}
            rowKey="receipt_number"
            dataSource={rec}
            columns={[
              { title: 'Receipt', dataIndex: 'receipt_number', key: 'n', render: (v: string) => <span style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 12 }}>{v}</span> },
              { title: 'Head', dataIndex: 'fee_head_name', key: 'h' },
              { title: 'Amount', dataIndex: 'amount_paise', key: 'a', align: 'right', render: (v: number) => `₹${paise(v)}` },
              { title: 'Method', dataIndex: 'method', key: 'm', render: (v: string) => <Tag>{v}</Tag> },
              { title: 'Date', dataIndex: 'received_on', key: 'd' },
            ]}
          />
        )}
      </Card>
    </Space>
  );
}

function HomeworkTab({ institution, student }: { institution: string; student: string }) {
  const q = useQuery({
    queryKey: ['guardian-ward-homework', institution, student],
    queryFn: () => productApi.getGuardianWardHomework(institution, student),
    enabled: !!institution && !!student,
  });
  if (q.isLoading) return <Skeleton active paragraph={{ rows: 4 }} />;
  const rows = q.data?.rows ?? [];
  if (rows.length === 0) return <Empty description="No homework assigned yet." style={{ padding: 20 }} />;
  return (
    <Space direction="vertical" size={10} style={{ width: '100%' }}>
      {rows.map((r: StudentHomeworkRow) => (
        <Card key={r.id} size="small">
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
            <b style={{ fontSize: 14 }}>{r.title}</b>
            <Tag>{r.subject_code}</Tag>
            <div style={{ flex: 1 }} />
            <Tag color={r.my_status === 'graded' ? 'success' : r.my_status === 'submitted' ? 'processing' : r.my_status === 'missing' ? 'error' : 'default'}>
              {r.my_status}
            </Tag>
          </div>
          <div style={{ color: '#5b6478', fontSize: 12, marginTop: 4 }}>
            {r.subject_name}{r.teacher_name ? ` · ${r.teacher_name}` : ''} · due <b>{r.due_on}</b>
          </div>
          {r.description && <div style={{ marginTop: 6, fontSize: 12.5, whiteSpace: 'pre-wrap' }}>{r.description}</div>}
          {r.my_grade_out_of_10 != null && <div style={{ marginTop: 6, fontSize: 12 }}>Grade: <b>{r.my_grade_out_of_10}/10</b></div>}
          {r.my_feedback && <div style={{ marginTop: 4, fontSize: 12, color: '#553a9c' }}>Feedback: {r.my_feedback}</div>}
        </Card>
      ))}
    </Space>
  );
}

function BroadcastsTab({ institution }: { institution: string }) {
  const q = useQuery({
    queryKey: ['guardian-broadcasts', institution],
    queryFn: () => productApi.getGuardianBroadcasts(institution),
    enabled: !!institution,
  });
  if (q.isLoading) return <Skeleton active paragraph={{ rows: 4 }} />;
  const broadcasts = q.data?.broadcasts ?? [];
  if (broadcasts.length === 0) return <Empty description="No broadcasts yet." style={{ padding: 20 }} />;
  return (
    <Space direction="vertical" size={10} style={{ width: '100%' }}>
      {broadcasts.map((b: GuardianBroadcast) => (
        <Card key={b.broadcast_id} size="small">
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
            <b style={{ fontSize: 14 }}>{b.subject}</b>
            <Tag>{b.channel}</Tag>
            <div style={{ flex: 1 }} />
            <Typography.Text type="secondary" style={{ fontSize: 11 }}>
              {b.sent_at ? new Date(b.sent_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : 'draft'}
            </Typography.Text>
          </div>
          <div style={{ marginTop: 8, fontSize: 13, color: '#26304a', whiteSpace: 'pre-wrap' }}>{b.body}</div>
        </Card>
      ))}
    </Space>
  );
}

function paise(n: number): string {
  return (n / 100).toLocaleString('en-IN', { maximumFractionDigits: 0 });
}
